/**
 * NO04 - Ordre List Component
 */

import { useRef, useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
    Box,
    CircularProgress,
    Checkbox,
    Chip,
    Button,
    Link,
    IconButton,
    Tooltip,
} from '@mui/material';
import { Download as DownloadIcon, List as ListIcon, AccessTimeOutlined as ClockIcon } from '@mui/icons-material';
import OrderDetailDialog from '../../../app/components/OrderDetailDialog';
import OrderLogDialog from '../../../app/components/OrderLogDialog';
import { makeStyles } from 'tss-react/mui';
import { AppState } from '../../../store/rootReducer';
import NO04 from '../../../models/NO04';
import * as actionsNO04 from '../../../store/actions/indexNO04';
import * as APICalls from '../../../felles/APICalls_NO04';
import CSharpUtils from '../../../felles/CSharpUtils';
import Utils from '../../../felles/Utils';

// Custom status colors matching the design (chip border + text color).
// Keys are the exact Norwegian labels the backend returns (see
// Autodata.Application/Handlers/GetOrderSummary/OrderSummaryMapper.MapOrderStatus).
const STATUS_STYLE: Record<string, { border: string; color: string; bg: string }> = {
    'Ferdig registrert':        { border: '#1976d2', color: '#1976d2', bg: '#e3f0fc' },
    'Venter godkjenning':       { border: '#1976d2', color: '#1976d2', bg: '#e3f0fc' },
    'Printet':                  { border: '#7b1fa2', color: '#7b1fa2', bg: '#f3e5f5' },
    'Pakket':                   { border: '#7b1fa2', color: '#7b1fa2', bg: '#f3e5f5' },
    'Sluppet forhåndsordre':    { border: '#7b1fa2', color: '#7b1fa2', bg: '#f3e5f5' },
    'Ferdig plukket':           { border: '#7b1fa2', color: '#7b1fa2', bg: '#f3e5f5' },
    'Ferdigmeldt':              { border: '#00796b', color: '#00796b', bg: '#e0f2f1' },
    'Lagerovf. (f.meldt)':      { border: '#00796b', color: '#00796b', bg: '#e0f2f1' },
    'Fakturert':                { border: '#00796b', color: '#00796b', bg: '#e0f2f1' },
    'Fmeld./KS-oppgjør':        { border: '#00796b', color: '#00796b', bg: '#e0f2f1' },
    'Forhånds ordre':           { border: '#00796b', color: '#00796b', bg: '#e0f2f1' },
    'Verkstedsord. (f.meldt)':  { border: '#00796b', color: '#00796b', bg: '#e0f2f1' },
    'Restreg. (f.meldt)':       { border: '#00796b', color: '#00796b', bg: '#e0f2f1' },
    'Påbegynt, ikke fullført':  { border: '#d32f2f', color: '#d32f2f', bg: '#fdecea' },
    'Kred-stopp, ikke print':   { border: '#d32f2f', color: '#d32f2f', bg: '#fdecea' },
    'Delferdigmeldt':           { border: '#e65100', color: '#e65100', bg: '#fff3e0' },
    'Utgårmeldt':               { border: '#757575', color: '#757575', bg: '#f5f5f5' },
    'Korr. etter f.melding':    { border: '#757575', color: '#757575', bg: '#f5f5f5' },
    'Retur til lev.':           { border: '#757575', color: '#757575', bg: '#f5f5f5' },
    'Påbegynt samleordre':      { border: '#757575', color: '#757575', bg: '#f5f5f5' },
    'Ufullstendig':             { border: '#757575', color: '#757575', bg: '#f5f5f5' },
};

const getStatusStyle = (status: string) =>
    STATUS_STYLE[status] ?? { border: '#9e9e9e', color: '#9e9e9e', bg: '#fafafa' };

const useStyles = makeStyles()((theme) => ({
    container: {
        margin: 0,
        borderRadius: 0,
        boxShadow: 'none',
        borderTop: '1px solid #e0e0e0',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
    },
    toolbarSection: {
        padding: theme.spacing(1, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #e0e0e0',
    },
    emptyState: {
        padding: theme.spacing(8),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    loadingContainer: {
        padding: theme.spacing(8),
        display: 'flex',
        justifyContent: 'center',
    },
    tableRow: {
        cursor: 'pointer',
        borderBottom: '1px solid #e8e8e8',
        '&:hover': { backgroundColor: theme.palette.action.hover },
    },
    tableRowRetur: {
        backgroundColor: '#fff6f6',
        cursor: 'pointer',
        borderBottom: '1px solid #e8e8e8',
        '&:hover': { backgroundColor: '#ffe8e8' },
    },
    cell: {
        fontSize: '0.82rem',
        padding: '8px 10px',
        whiteSpace: 'nowrap',
        border: 'none',
    },
    cellTop: {
        fontSize: '0.80rem',
        padding: '5px 7px 2px 7px',
        whiteSpace: 'nowrap',
        border: 'none',
        verticalAlign: 'bottom',
    },
    ordreLink: {
        color: theme.palette.primary.main,
        fontWeight: 700,
        fontSize: '0.88rem',
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
    },
    dateTime: {
        color: theme.palette.text.secondary,
        fontSize: '0.78rem',
        marginLeft: 6,
    },
    label: {
        color: theme.palette.text.secondary,
        fontSize: '0.78rem',
        marginRight: 2,
    },
    value: {
        fontSize: '0.82rem',
        fontWeight: 500,
    },
    fraktLine: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        padding: '0px 10px 6px 10px',
        border: 'none',
    },
    negative: { color: theme.palette.error.main, fontWeight: 600 },
    positive: { color: theme.palette.text.primary, fontWeight: 500 },
    pct: {
        fontSize: '0.78rem',
        fontWeight: 600,
        minWidth: 52,
        textAlign: 'right',
    },
    loadMoreContainer: {
        padding: theme.spacing(2),
        display: 'flex',
        justifyContent: 'center',
    },
    selectAllRow: {
        padding: theme.spacing(0.5, 2),
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
    },
}));

export default function NO04List() {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const isFetchingRef = useRef(false);

    const orders = useSelector((state: AppState) => state.NO04Ordre.orders);
    const loading = useSelector((state: AppState) => state.NO04Ordre.loading);
    const loadingMore = useSelector((state: AppState) => state.NO04Ordre.loadingMore);
    const hasMore = useSelector((state: AppState) => state.NO04Ordre.hasMore);
    const currentPage = useSelector((state: AppState) => state.NO04Ordre.currentPage);
    const lastSearchParams = useSelector((state: AppState) => state.NO04Ordre.lastSearchParams);
    const error = useSelector((state: AppState) => state.NO04Ordre.error);
    const orderDetail = useSelector((state: AppState) => state.NO04Ordre.orderDetail);
    const orderDetailLoading = useSelector((state: AppState) => state.NO04Ordre.orderDetailLoading);

    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [dialogOpen, setDialogOpen] = useState(false);

    const [logDialogOpen, setLogDialogOpen] = useState(false);
    const [logLoading, setLogLoading] = useState(false);
    const [logEntries, setLogEntries] = useState<any[]>([]);
    const [logOrder, setLogOrder] = useState<NO04 | null>(null);

    const displayOrders = useMemo(() => orders.slice(), [orders]);

    const handleOrdreNrClick = (ordreNr: string) => {
        dispatch(actionsNO04.NO04OrderDetailRequest(CSharpUtils.T_companyID, ordreNr));
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        dispatch(actionsNO04.NO04OrderDetailClear());
    };

    const handleOrderLogClick = async (order: NO04) => {
        setLogOrder(order);
        setLogDialogOpen(true);
        setLogLoading(true);
        try {
            const res = await APICalls.fetchOrderLog(CSharpUtils.T_companyID, order.ordreNr);
            setLogEntries(res.data?.entries ?? []);
        } catch {
            setLogEntries([]);
        } finally {
            setLogLoading(false);
        }
    };

    const handleLogDialogClose = () => {
        setLogDialogOpen(false);
        setLogEntries([]);
        setLogOrder(null);
    };

    const handleFraktClick = async (fraktEdi: string) => {
        try {
            const companyId = lastSearchParams?.companyId ?? CSharpUtils.T_companyID;
            const response = await APICalls.getInvoicePDF(companyId, fraktEdi);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch {
            alert('Kunne ikke laste faktura PDF. Vennligst prøv igjen.');
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: '2-digit' });
    };

    const formatTime = (tid: string) => {
        if (!tid) return '';
        const t = tid.toString().padStart(6, '0');
        return `${t.substring(0, 2)}:${t.substring(2, 4)}`;
    };

    const formatAmount = (amount: number) =>
        new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

    const formatPct = (pct: number | null) => {
        if (pct == null) return null;
        const formatted = new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(pct);
        return `${formatted}%`;
    };

    const getTrackingUrl = (trackingNumber: string, carrierCode: string): string => {
        const tn = trackingNumber.trim();
        const cc = carrierCode.trim().toUpperCase();
        if (cc === 'NC' || cc === 'LF')
            return `http://amphora.norcargo.no/track/trackntrace/simple_fraktbrevframeset.jsp?count=1&intern=ja&fraktbrevnr=${tn}`;
        if (cc === 'LG')
            return `http://myschenker.linjegods.no/DavDB/srv_spor_kriterier.html?fraktnr=${tn}&lang=nor`;
        if (cc === 'EK')
            return `http://www.transportsiden.no/Sporing/FrbrsokBilde.aspx?frbrnr=${tn}&transp=eek`;
        if (cc === 'TP')
            return `https://www.postnord.no/pakkesporing?shipmentId=${tn}`;
        if (cc === 'RA')
            return `/system/system/Sped_ramberg.php?sndnr=${tn}`;
        return `https://sporing.bring.no/sporing/${tn}`;
    };

    const isRetur = (order: NO04) => order.ordreNr.startsWith('9');

    const allSelected = selected.size === displayOrders.length && displayOrders.length > 0;

    const handleSelectAll = (checked: boolean) => {
        setSelected(checked ? new Set(displayOrders.map((o) => o.ordreNr)) : new Set());
    };

    const handleSelectRow = (ordreNr: string, checked: boolean) => {
        const next = new Set(selected);
        checked ? next.add(ordreNr) : next.delete(ordreNr);
        setSelected(next);
    };

    const [exportingLines, setExportingLines] = useState(false);

    const triggerDownload = (content: string, filename: string) => {
        const bom = '\uFEFF';
        const el = document.createElement('a');
        el.setAttribute('href', `data:text/csv;charset=utf-8,${encodeURIComponent(bom + content)}`);
        el.setAttribute('download', filename);
        document.body.appendChild(el);
        el.click();
        document.body.removeChild(el);
    };

    // Excel — exports the current order list
    const exportOrdersToCSV = () => {
        const toExport = selected.size > 0
            ? displayOrders.filter((o) => selected.has(o.ordreNr))
            : displayOrders;
        if (toExport.length === 0) return;

        const headers = ['OrdreNr','Dato','Tid','Kundenr','Navn','RekvNr','OM','Status','Bestilt','Levert','Beløp','BRF%','Faktura','Sporing','SendeMåte','BK','BM'];
        const rows = toExport.map((o) => [
            Utils.csvText(o.ordreNr),
            Utils.csvText(o.dato ? new Date(o.dato).toLocaleDateString('nb-NO') : ''),
            Utils.csvText(o.tid ? `${o.tid.toString().padStart(6,'0').substring(0,2)}:${o.tid.toString().padStart(6,'0').substring(2,4)}` : ''),
            Utils.csvText(o.kundenr),
            Utils.csvText(o.navn),
            Utils.csvText(o.rekvNr),
            Utils.csvText(o.om),
            Utils.csvText(o.status),
            // Numeric — left unwrapped (not csvText) so Excel can SUM these columns.
            Utils.csvNumber(o.antBestilt),
            Utils.csvNumber(o.antLevert),
            Utils.csvNumber(o.belop),
            Utils.csvNumber(o.brfProsent),
            Utils.csvText(o.faktura),
            Utils.csvText(o.fraktEdi),
            Utils.csvText(o.sendeMote),
            Utils.csvText(o.bk),
            Utils.csvText(o.betalingsKode),
        ].join(';'));

        triggerDownload([headers.join(';'), ...rows].join('\n'), `Ordre_${Date.now()}.csv`);
    };

    // Med ordrelinjer — fetches detail per order and exports flattened lines
    const exportOrdersWithLines = async () => {
        const toExport = selected.size > 0
            ? displayOrders.filter((o) => selected.has(o.ordreNr))
            : displayOrders;
        if (toExport.length === 0) return;

        setExportingLines(true);
        try {
            const { fetchOrderDetail } = await import('../../../felles/APICalls_NO04');
            const companyId = Number(CSharpUtils.T_companyID);

            const headers = ['OrdreNr','Dato','Kundenr','Navn','RekvNr','Status','BK','BM','Alfa','Artikkel','Bestilt','Levert','Tekst','VPris','Rabatt','NPris','Tillegg'];

            const rowGroups = await Utils.mapWithConcurrency(toExport, 8, async (order): Promise<string[]> => {
                try {
                    const res = await fetchOrderDetail(companyId, order.ordreNr);
                    const detail = res.data;
                    const dato = order.dato ? new Date(order.dato).toLocaleDateString('nb-NO') : '';
                    const bk = detail?.processingCode ?? '';
                    const bm = detail?.BETMET ?? '';
                    const varer: any[] = detail?.varer ?? [];
                    const orderCols = [order.ordreNr, dato, order.kundenr, order.navn, order.rekvNr, order.status, bk, bm].map(Utils.csvText);
                    if (varer.length === 0) {
                        return [[...orderCols, '', '', '', '', '', '', '', '', ''].join(';')];
                    }
                    // Bestilt/Levert/VPris/Rabatt/NPris/Tillegg left unwrapped (not csvText) so Excel can SUM these columns.
                    return varer.map((v: any) => [
                        ...orderCols,
                        Utils.csvText(v.alfa), Utils.csvText(v.artikkel),
                        Utils.csvNumber(v.bestilt), Utils.csvNumber(v.levert),
                        Utils.csvText(v.tekst),
                        Utils.csvNumber(v.vpris), Utils.csvNumber(v.rab1), Utils.csvNumber(v.npris), Utils.csvNumber(v.tillegg),
                    ].join(';'));
                } catch {
                    const errCols = [order.ordreNr, '', order.kundenr, order.navn, '', order.status, '', ''].map(Utils.csvText);
                    return [[...errCols, Utils.csvText('FEIL'), '', '', '', '', '', '', '', ''].join(';')];
                }
            });
            const rows = rowGroups.flat();

            triggerDownload([headers.join(';'), ...rows].join('\n'), `Ordre_MedLinjer_${Date.now()}.csv`);
        } finally {
            setExportingLines(false);
        }
    };

    // Infinite scroll
    useEffect(() => {
        isFetchingRef.current = loadingMore;
    }, [loadingMore]);

    useEffect(() => {
        const handleScroll = () => {
            const container = tableContainerRef.current;
            if (!container || isFetchingRef.current || !hasMore || !lastSearchParams) return;
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollHeight - scrollTop - clientHeight < 150) {
                isFetchingRef.current = true;
                dispatch(actionsNO04.NO04LoadMoreRequest({
                    ...lastSearchParams,
                    page: currentPage + 1,
                    pageSize: 100,
                }));
            }
        };
        const container = tableContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [hasMore, currentPage, lastSearchParams, loading, dispatch]);

    if (loading) {
        return (
            <Paper className={classes.container}>
                <Box className={classes.loadingContainer}><CircularProgress /></Box>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper className={classes.container}>
                <Box className={classes.emptyState}>
                    <Typography color="error">{error}</Typography>
                </Box>
            </Paper>
        );
    }

    if (orders.length === 0) {
        return (
            <Paper className={classes.container}>
                <Box className={classes.emptyState}>
                    <Typography variant="h6">Ingen ordre funnet</Typography>
                    <Typography variant="body2">Søk på ordre siste 5 år</Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper className={classes.container}>
            {/* Toolbar: Velg alle + Excel + Med ordrelinjer */}
            <Box className={classes.toolbarSection}>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSelectAll(!allSelected)}
                    sx={{ fontSize: '0.8rem', textTransform: 'none', minWidth: 'unset', borderColor: '#d30535', color: '#d30535', '&:hover': { borderColor: '#b0022c', color: '#b0022c', backgroundColor: 'rgba(211,5,53,0.04)' } }}
                >
                    Velg alle
                </Button>
                <Box display="flex" gap={1}>
                    <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportOrdersToCSV}
                        sx={{ color: '#555', borderColor: '#aaa', '&:hover': { borderColor: '#555', backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                        Excel
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={exportingLines ? <CircularProgress size={14} /> : <ListIcon />}
                        onClick={exportOrdersWithLines}
                        disabled={exportingLines}
                        sx={{ color: '#555', borderColor: '#aaa', '&:hover': { borderColor: '#555', backgroundColor: 'rgba(0,0,0,0.04)' } }}
                    >
                        Med ordrelinjer
                    </Button>
                </Box>
            </Box>

            {/* List */}
            <TableContainer ref={tableContainerRef} sx={{ flex: 1, overflowY: 'auto', minHeight: 0, scrollbarGutter: 'stable' }}>
                <Table size="small" sx={{ tableLayout: 'auto' }}>
                    <TableBody>
                        {displayOrders.map((order: NO04, index: number) => {
                            const pct = formatPct(order.brfProsent);
                            const hasFrakt = !!order.fraktEdi;
                            return (
                                <TableRow
                                    key={`${order.ordreNr}-${index}`}
                                    className={isRetur(order) ? classes.tableRowRetur : classes.tableRow}
                                    sx={{ '& td': { verticalAlign: 'middle' } }}
                                >
                                    {/* Checkbox */}
                                    <TableCell padding="checkbox" className={classes.cell} sx={{ pl: 1 }}>
                                        <Checkbox
                                            size="small"
                                            checked={selected.has(order.ordreNr)}
                                            onChange={(e) => handleSelectRow(order.ordreNr, e.target.checked)}
                                        />
                                    </TableCell>

                                    {/* OrdreNr + date/time */}
                                    <TableCell className={classes.cell} sx={{ minWidth: 160 }}>
                                        <Box display="flex" alignItems="baseline" gap={0.5}>
                                            <Link
                                                className={classes.ordreLink}
                                                underline="hover"
                                                onClick={() => handleOrdreNrClick(order.ordreNr)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {order.ordreNr}
                                            </Link>
                                            <span className={classes.dateTime}>
                                                {formatDate(order.dato)}{order.tid ? ` ${formatTime(order.tid)}` : ''}
                                            </span>
                                        </Box>
                                        {order.faktura && (
                                            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.3 }}>
                                                <span style={{ marginRight: 2 }}>Faktura:</span>
                                                <Link
                                                    underline="hover"
                                                    sx={{ fontSize: '0.75rem', cursor: 'pointer' }}
                                                    onClick={() => handleFraktClick(order.faktura)}
                                                >
                                                    {order.faktura}
                                                </Link>
                                            </Box>
                                        )}
                                    </TableCell>

                                    {/* Kundenr */}
                                    <TableCell className={classes.cell} sx={{ minWidth: 100 }}>
                                        <span className={classes.label}>Kunde:</span>
                                        <span className={classes.value}>{order.kundenr}</span>
                                    </TableCell>

                                    {/* Navn + Frakt/Sporing */}
                                    <TableCell className={classes.cell} sx={{ minWidth: 160, maxWidth: 260 }}>
                                        <Box>
                                            <Box sx={{ fontSize: '0.82rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }} title={order.navn}>
                                                {order.navn}
                                            </Box>
                                            {hasFrakt && (
                                                <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.3, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                                    {order.speditor && (
                                                        <span style={{ marginRight: 4 }}>{order.speditor} –</span>
                                                    )}
                                                    Frakt/Sporing:{' '}
                                                    {order.speditor ? (
                                                        <Link
                                                            href={getTrackingUrl(order.fraktEdi, order.speditor)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            underline="hover"
                                                            sx={{ fontSize: '0.75rem' }}
                                                        >
                                                            {order.fraktEdi}
                                                        </Link>
                                                    ) : (
                                                        order.fraktEdi
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </TableCell>

                                    {/* Rekv.nr — no width cap: table-layout:auto already sizes this column
                                        from the widest Rekv value across all (non-virtualized) rows, so
                                        capping it here only truncated it needlessly when there was room. */}
                                    <TableCell className={classes.cell}>
                                        <span className={classes.label}>Rekv:</span>
                                        <span className={classes.value}>{order.rekvNr}</span>
                                    </TableCell>

                                    {/* Status chip */}
                                    <TableCell className={classes.cell} sx={{ maxWidth: 140 }}>
                                        {(() => {
                                            const s = getStatusStyle(order.status);
                                            return (
                                                <Chip
                                                    label={order.status}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: '0.72rem',
                                                        height: 22,
                                                        maxWidth: 130,
                                                        borderColor: s.border,
                                                        color: s.color,
                                                        backgroundColor: s.bg,
                                                        fontWeight: 600,
                                                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                                                    }}
                                                />
                                            );
                                        })()}
                                    </TableCell>

                                    {/* OM */}
                                    <TableCell className={classes.cell}>
                                        <span className={classes.label}>OM:</span>
                                        <span className={classes.value}>{order.om}</span>
                                    </TableCell>

                                    {/* Best + Lev */}
                                    <TableCell className={classes.cell} sx={{ width: '1%' }}>
                                        <span className={classes.label}>Best:</span>
                                        <span className={classes.value}>{Utils.round2(order.antBestilt)}</span>
                                        {'  '}
                                        <span className={classes.label}>Lev:</span>
                                        <span
                                            className={classes.value}
                                            style={{ color: order.antLevert > 0 ? '#1976d2' : undefined }}
                                        >
                                            {Utils.round2(order.antLevert)}
                                        </span>
                                    </TableCell>

                                    {/* Beløp */}
                                    <TableCell
                                        className={classes.cell}
                                        align="right"
                                        sx={{ minWidth: 80 }}
                                    >
                                        <span
                                            className={order.belop < 0 ? classes.negative : classes.positive}
                                        >
                                            {formatAmount(order.belop)} kr
                                        </span>
                                    </TableCell>

                                    {/* % */}
                                    <TableCell className={classes.cell} align="right" sx={{ minWidth: 60 }}>
                                        {pct && (
                                            <span
                                                className={classes.pct}
                                                style={{ color: order.brfProsent < 0 ? '#d32f2f' : undefined }}
                                            >
                                                {pct}
                                            </span>
                                        )}
                                    </TableCell>

                                    {/* Ordrelogg */}
                                    <TableCell className={classes.cell} align="right" sx={{ minWidth: 40 }}>
                                        <Tooltip title="Ordrelogg">
                                            <IconButton size="small" onClick={() => handleOrderLogClick(order)}>
                                                <ClockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {loadingMore && (
                <Box className={classes.loadMoreContainer}><CircularProgress size={24} /></Box>
            )}

            {/* Order Detail Dialog */}
            <OrderDetailDialog open={dialogOpen} loading={orderDetailLoading} orderDetail={orderDetail} onClose={handleDialogClose} />

            {/* Order Log Dialog */}
            <OrderLogDialog
                open={logDialogOpen}
                loading={logLoading}
                orderNumber={logOrder?.ordreNr ?? ''}
                customerName={logOrder?.navn}
                customerNumber={logOrder?.kundenr}
                entries={logEntries}
                onClose={handleLogDialogClose}
            />
        </Paper>
    );
}

